export type Seat = "A" | "B";
export type Stance = "dominant" | "submissive" | "switch";
export type Framing = "shared" | "directional" | "role-paired";
export type StanceDefault =
  | "both"
  | "dominant-first"
  | "dominant-second";

export const INTEREST_OPTIONS = [
  "need",
  "yearn",
  "want",
  "open",
  "dislike",
  "hard-limit",
] as const;

export const EXPERIENCE_OPTIONS = [
  "expert",
  "confident",
  "familiar",
  "learning",
  "once",
  "never",
] as const;

export type Interest = (typeof INTEREST_OPTIONS)[number];
export type Experience = (typeof EXPERIENCE_OPTIONS)[number];

export const MAX_PERSONAL_CONTEXT_LENGTH = 4_001;

export interface Category {
  id: string;
  number: number;
  name: string;
}

export interface CatalogueItem {
  id: string;
  term: string;
  definition: string;
  aliases: string[];
  framing: Framing;
  stanceDefault: StanceDefault;
  perspectives: string[];
  categoryId: string;
  categoryName: string;
  subgroupId: string | null;
  subgroupName: string | null;
  custom?: boolean;
  createdBy?: Seat;
  locked?: boolean;
  customVersion?: number;
}

export interface PerspectiveAnswer {
  enabled: boolean;
  interest: Interest | null;
  experience: Experience | null;
}

export interface ParticipantAnswer {
  itemId: string;
  perspectives: Record<string, PerspectiveAnswer>;
  responseScaleVersion?: 2;
  meaning: string;
  context: string;
  important: boolean;
  savedAt: number | null;
  updatedAt: number;
  version: number;
  changedAfterReveal: boolean;
}

export interface AnswersImportPayload {
  answers: ParticipantAnswer[];
  customEntries: CatalogueItem[];
}

export interface SharedNote {
  itemId: string;
  text: string;
  important: boolean;
  updatedAt: number;
  version: number;
}

export interface Participant {
  name: string;
  stance: Stance;
}

export interface LocalSession {
  mode: "local";
  roomId: string;
  catalogueVersion: number;
  createdAt: number;
  expiresAt: number;
  retainedIndefinitely?: boolean;
  participants: Record<Seat, Participant>;
  activeSeat: Seat;
  answers: Record<Seat, Record<string, ParticipantAnswer>>;
  notes: Record<string, SharedNote>;
  customEntries: CatalogueItem[];
  activeItemId: string;
  collapsedCategories: string[];
  lastUpdatedAt: number;
}

export type LocalRoomCopySelection = Seat | "both";

export interface TakeLocalRoomOnlineOptions {
  selection: LocalRoomCopySelection;
  newPartner: Participant;
}

export interface RemoteCredentials {
  roomId: string;
  seat: Seat;
  capability: string;
  key: string;
}

export interface RemoteRoomView {
  mode: "remote";
  roomId: string;
  catalogueVersion: number;
  createdAt: number;
  expiresAt: number;
  participants: Record<Seat, Participant>;
  profileVersions: Record<Seat, number>;
  seat: Seat;
  ownAnswers: Record<string, ParticipantAnswer>;
  partnerAnswers: Record<string, ParticipantAnswer>;
  partnerSaved: string[];
  notes: Record<string, SharedNote>;
  customEntries: CatalogueItem[];
  activeItemId: string;
  partnerActiveItemId: string | null;
  connected: boolean;
  terminalState: "expired" | "deleted" | null;
}

export type SessionView = LocalSession | RemoteRoomView;

export interface RoomSnapshotWire {
  type: "snapshot";
  roomId: string;
  catalogueVersion: number;
  createdAt: number;
  expiresAt: number;
  profileEnvelopes: Partial<Record<Seat, EncryptedEnvelope>>;
  profileVersions: Partial<Record<Seat, number>>;
  ownAnswers: Array<{
    itemId: string;
    envelope: EncryptedEnvelope;
    version: number;
    savedAt: number;
    changedAfterReveal: boolean;
  }>;
  partnerAnswers: Array<{
    itemId: string;
    envelope: EncryptedEnvelope;
    version: number;
    savedAt: number;
    changedAfterReveal: boolean;
  }>;
  partnerSaved: string[];
  partnerActiveItemId: string | null;
  notes: Array<{
    itemId: string;
    envelope: EncryptedEnvelope;
    version: number;
  }>;
  customEntries: Array<{
    entryId: string;
    envelope: EncryptedEnvelope;
    version: number;
    locked: boolean;
    createdBy: Seat;
  }>;
}

export interface EncryptedEnvelope {
  iv: string;
  ciphertext: string;
}

export type FilterKey =
  | "all"
  | "unanswered"
  | "pending"
  | "differences"
  | "notes"
  | "important";

export interface ExportRecord {
  item: CatalogueItem;
  note: SharedNote | null;
  answers: Record<Seat, ParticipantAnswer | null | "redacted">;
  revealState: "unanswered" | "pending" | "revealed";
}
